/**
 * useStableCallback — A callback whose identity is stable across renders.
 *
 * `useCallback` only memoizes when the dependency array changes; if any
 * dependency varies, the function identity churns and downstream
 * `useEffect` / `useMemo` blocks invalidate unnecessarily.
 *
 * `useStableCallback` returns a function whose reference never changes
 * for the lifetime of the component, but whose body always invokes the
 * *latest* implementation passed in. Use this when the callback is
 * passed to children or used as a `useEffect` dependency, and you want
 * to avoid re-running effects when the closure body would have updated
 * harmlessly.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useRef } from "react";

export function useStableCallback<TArgs extends readonly unknown[], TReturn>(
  callback: (...args: TArgs) => TReturn,
): (...args: TArgs) => TReturn {
  const ref = useRef(callback);
  ref.current = callback;
  return useCallback((...args: TArgs): TReturn => ref.current(...args), []);
}
